import {
  Box,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';

interface ColumnFilterPalette {
  textPrimary: string;
  primary: string;
  boxBackground: string;
}

interface ColumnTextFilterProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  palette: ColumnFilterPalette;
  enableDarkTheme: boolean;
}

const filterShellSx = (
  enableDarkTheme: boolean,
  palette: ColumnFilterPalette,
  isActive: boolean
) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  width: '100%',
  minWidth: 0,
  borderRadius: '6px',
  paddingLeft: '8px',
  paddingRight: '2px',
  minHeight: '26px',
  transition: 'border-color 0.15s ease, background-color 0.15s ease',
  backgroundColor: enableDarkTheme
    ? 'rgba(255, 255, 255, 0.05)'
    : 'rgba(0, 0, 0, 0.03)',
  border: `1px solid ${
    isActive
      ? palette.primary
      : enableDarkTheme
      ? 'rgba(255, 255, 255, 0.12)'
      : 'rgba(0, 0, 0, 0.1)'
  }`,
  '&:hover': {
    backgroundColor: enableDarkTheme
      ? 'rgba(255, 255, 255, 0.09)'
      : 'rgba(0, 0, 0, 0.06)',
  },
  '&:focus-within': {
    borderColor: palette.primary,
  },
});

const ClearButton = ({
  onClear,
  palette,
}: {
  onClear: () => void;
  palette: ColumnFilterPalette;
}) => (
  <IconButton
    onClick={onClear}
    size="small"
    aria-label="clear filter"
    sx={{
      padding: '2px',
      color: palette.textPrimary,
      opacity: 0.7,
      '&:hover': { opacity: 1 },
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  </IconButton>
);

export const ColumnTextFilter = ({
  value,
  onChange,
  placeholder,
  palette,
  enableDarkTheme,
}: ColumnTextFilterProps) => {
  return (
    <CustomMuiThemeProvider>
      <Box sx={filterShellSx(enableDarkTheme, palette, value.length > 0)}>
        <InputBase
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder={placeholder}
          sx={{
            flex: 1,
            fontSize: '11px',
            fontWeight: 400,
            color: palette.textPrimary,
            '& input::placeholder': {
              color: palette.textPrimary,
              opacity: 0.5,
            },
          }}
        />
        {value.length > 0 && (
          <ClearButton
            palette={palette}
            onClear={() => {
              onChange('');
            }}
          />
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};

interface ColumnSelectFilterProps {
  value: string;
  onChange: (value: string) => void;
  options: string[];
  placeholder: string;
  palette: ColumnFilterPalette;
  enableDarkTheme: boolean;
}

export const ColumnSelectFilter = ({
  value,
  onChange,
  options,
  placeholder,
  palette,
  enableDarkTheme,
}: ColumnSelectFilterProps) => {
  return (
    <CustomMuiThemeProvider>
      <Box sx={filterShellSx(enableDarkTheme, palette, value.length > 0)}>
        <Select
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) =>
            selected ? (
              <Typography sx={{ fontSize: '11px', color: palette.textPrimary }}>
                {selected}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: '11px',
                  color: palette.textPrimary,
                  opacity: 0.5,
                }}
              >
                {placeholder}
              </Typography>
            )
          }
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: '11px',
            fontWeight: 400,
            color: palette.textPrimary,
            // The select must fill the shell and leave room for the caret,
            // otherwise the label is clipped down to just the arrow.
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset !important',
              paddingTop: '2px',
              paddingBottom: '2px',
              paddingLeft: 0,
              paddingRight: '18px !important',
            },
            '& .MuiSelect-icon': {
              display: value ? 'none' : 'block',
              right: 0,
              color: palette.textPrimary,
              opacity: 0.6,
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
        {value.length > 0 && (
          <ClearButton
            palette={palette}
            onClear={() => {
              onChange('');
            }}
          />
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
};
